import React, {useCallback, useContext, useEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import PropTypes from "prop-types";
import {faPlus, faSearch, faEllipsisVertical} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import styled from "styled-components";

// Contexts
import {AuthContext} from "../../contexts/auth.js";

// Hooks
import useMountedState from "../../hooks/useMountedState.js";
import useApi from "../../hooks/useApi.js";

// Components
import CustomTable from "./CustomTable.js";
import CustomTableSharepoint from "./CustomTableSharepoint.js";
import ModalCustomTable from "./ModalCustomTable.js";
import ModalViewAllRows from "./ModalViewAllRows.js";
import ModalRowPreview from "./ModalRowPreview.js";

// Styles
import {
  Button,
  Loader,
  Text,
  SearchWrapper,
  Search,
  SearchIcon
} from "../../style/components/general.js";
import {border, pad, radius, transition} from "../../style/components/variables.js";
import {flex, z} from "../../style/components/mixins.js";

const CustomTables = ({
  facility,
  inGlobalTables = false,
  setHasNoTables,
  query = "",
  globalToggleAll,
  setGlobalToggleAll,
  setGlobalAllOpen,
  actionMenuEl
}) => {
  const isMounted = useMountedState();

  const {roleCanAccessResource} = useContext(AuthContext);

  const {api} = useApi("custom-tables");
  const {api: apiRows} = useApi("custom-table-rows");

  const initialized = useRef(false);
  const currentTableUpdater = useRef(undefined);
  const tablesOpen = useRef({});

  const [loading, setLoading] = useState(false);
  const [overflow, setOverflow] = useState(false);
  const [tables, setTables] = useState();
  const [active, setActive] = useState(1);
  const [target, setTarget] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [tableQuery, setTableQuery] = useState(false);
  const [triggerToggleAll, setTriggerToggleAll] = useState(false);
  const [allOpen, setAllOpen] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const actionsMenuRef = useRef(null);
  // Modals
  const [showModalEdit, setShowModalEdit] = useState(false);
  const [showModalViewAll, setShowModalViewAll] = useState(false);
  const [showModalPreview, setShowModalPreview] = useState(false);
  const [targetRow, setTargetRow] = useState();

  const displayTables = tables?.filter(
    ({label, name}) =>
      !tableQuery ||
      label?.toLowerCase().includes(tableQuery.toLowerCase()) ||
      name?.toLowerCase().includes(tableQuery.toLowerCase())
  );

  useEffect(() => {
    if (setHasNoTables && tables) setHasNoTables(tables.length === 0);
  }, [setHasNoTables, tables]);

  useEffect(() => {
    const {hash} = window.location;
    if (hash) {
      const rowId = parseInt(hash.replace("#", ""), 10);
      if (rowId && !Number.isNaN(rowId)) {
        apiRows.callGet(rowId).then(({status, data}) => {
          if (status === 200) {
            setTargetRow(data);
            setShowModalPreview(true);
            window.history.replaceState(null, null, " ");
          }
        });
      }
    }
  }, [apiRows]);

  const getCustomTables = useCallback(
    (page, refresh) => {
      setLoading(true);

      if (refresh) {
        setCurrentPage(1);
        setPages(1);
        setTables(null);
      }

      const params = {
        page: page || 1,
        limit: 20
      };

      if (facility?.id) params.facilityId = facility.id;
      else params.global = true;

      if (query)
        params.filter = JSON.stringify({
          Search: query
        });

      api
        .callGet(null, params)
        .then(({status, data}) => {
          if (status === 200) {
            setCurrentPage(data.page);
            setPages(data.pages);
            setTables(prev => (prev ? [...prev, ...data.tables] : data.tables));
            initialized.current = false;
          }
        })
        .finally(() => {
          setLoading(false);
          setOverflow(false);
        });
    },
    [api, facility, query]
  );

  const handleScroll = useCallback(() => {
    const {scrollHeight, clientHeight, scrollTop} = document.documentElement;
    const maxScrollTop = scrollHeight - clientHeight;

    if (scrollTop + 1 >= maxScrollTop && currentPage < pages && !loading) setOverflow(true);
  }, [currentPage, loading, pages]);

  useEffect(() => {
    document.addEventListener("scroll", handleScroll);
    return () => document.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    if (tables && overflow) getCustomTables(currentPage + 1);
  }, [tables, overflow, currentPage, getCustomTables]);

  // Initial Load
  useEffect(() => {
    if (isMounted() && !initialized.current) getCustomTables(undefined, true);
  }, [isMounted, getCustomTables]);

  useEffect(() => {
    if (isMounted() && query) getCustomTables(1, true);
  }, [isMounted, query, getCustomTables]);

  const handleEditTable = t => {
    setTarget(t);
    setShowModalEdit(true);
  };

  const handleViewAll = (t, update) => {
    currentTableUpdater.current = update;
    setTarget(t);
    setShowModalViewAll(true);
  };

  const onOpen = useCallback(
    (state, tableId) => {
      tablesOpen.current[tableId] = state;
      if (displayTables?.every(({id}) => tablesOpen.current[id])) {
        if (setGlobalAllOpen) setGlobalAllOpen(true);
        setAllOpen(true);
      } else {
        if (setGlobalAllOpen) setGlobalAllOpen(false);
        setAllOpen(false);
      }
      if (setGlobalToggleAll) setGlobalToggleAll(false);
      setTriggerToggleAll(false);
    },
    [displayTables, setGlobalToggleAll, setGlobalAllOpen]
  );

  useEffect(() => {
    if (displayTables?.every(({id}) => tablesOpen.current[id])) {
      if (setGlobalAllOpen) setGlobalAllOpen(true);
      setAllOpen(true);
    } else {
      if (setGlobalAllOpen) setGlobalAllOpen(false);
      setAllOpen(false);
    }
    if (setGlobalToggleAll) setGlobalToggleAll(false);
    setTriggerToggleAll(false);
  }, [displayTables, tableQuery, query, setGlobalToggleAll, setGlobalAllOpen]);

  const handleTableSearch = e => {
    setTableQuery(e.target.value);
  };

  useEffect(() => {
    const handleClickOutside = e => {
      if (!showActionsMenu) return;
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(e.target)) {
        setShowActionsMenu(false);
      }
    };

    const handleEscape = e => {
      if (e.key === "Escape") setShowActionsMenu(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [showActionsMenu]);

  const noTables = displayTables?.length === 0;

  const handledOpen = useRef(false);

  const actionsMenu = (
    <ActionsMenuWrapper ref={actionsMenuRef}>
      <ActionMenuButton
        title="More"
        type="button"
        $active={!!tableQuery || showActionsMenu}
        aria-label="Document actions"
        aria-expanded={showActionsMenu}
        aria-haspopup="true"
        data-testid="customTables.moreActions"
        onClick={() => setShowActionsMenu(prev => !prev)}>
        <FontAwesomeIcon icon={faEllipsisVertical} />
      </ActionMenuButton>
      {showActionsMenu && (
        <ActionsMenu>
          <MenuSearch>
            <SearchWrapper>
              <SearchIcon>
                <FontAwesomeIcon icon={faSearch} />
              </SearchIcon>
              <StyledSearch
                type="text"
                name="search"
                placeholder="Tables..."
                autoFocus
                value={tableQuery || ""}
                onChange={handleTableSearch}
              />
            </SearchWrapper>
          </MenuSearch>
          {!noTables && (
            <ActionItem
              type="button"
              title={allOpen ? "Collapse All Tables" : "Expand All Tables"}
              onClick={() => {
                setTriggerToggleAll(true);
                setShowActionsMenu(false);
              }}>
              {allOpen ? "Collapse All Tables" : "Expand All Tables"}
            </ActionItem>
          )}
        </ActionsMenu>
      )}
    </ActionsMenuWrapper>
  );

  return (
    <>
      <OutermostWrapper>
        {!inGlobalTables &&
          (actionMenuEl
            ? createPortal(actionsMenu, actionMenuEl)
            : !!tables &&
              (!noTables || tableQuery) && <InlineMenu>{actionsMenu}</InlineMenu>)}

        {noTables && (
          <NotFound>
            <Text>
              No {inGlobalTables ? "Global" : "Custom"} Tables{" "}
              {query || tableQuery ? "were found" : "have been created"}
              {facility?.id && " for this facility"}.
            </Text>
          </NotFound>
        )}

        {displayTables?.map((table, index) => (
          <div id={`t${table.id}`} key={table.id}>
            {!table.sharepointFolderId ? (
              <CustomTable
                index={index + 1}
                table={table}
                singleActive={false}
                active={active}
                setActive={setActive}
                handleViewAll={handleViewAll}
                handleEditTable={handleEditTable}
                reload={() => getCustomTables(null, true)}
                inGlobalTables={inGlobalTables}
                onOpen={onOpen}
                triggerOpen={triggerToggleAll || globalToggleAll ? !allOpen : undefined}
                handledOpen={handledOpen}
              />
            ) : (
              <CustomTableSharepoint
                index={index + 1}
                table={table}
                singleActive={false}
                active={active}
                setActive={setActive}
                handleViewAll={handleViewAll}
                handleEditTable={handleEditTable}
                reload={() => getCustomTables(null, true)}
                inGlobalTables={inGlobalTables}
                onOpen={onOpen}
                triggerOpen={triggerToggleAll || globalToggleAll ? !allOpen : undefined}
                handledOpen={handledOpen}
              />
            )}
          </div>
        ))}

        <LoadWrapper show={loading}>
          <Loader />
        </LoadWrapper>
      </OutermostWrapper>

      {!inGlobalTables &&
        roleCanAccessResource("custom_table", "create") &&
        !facility?.isDeleted && (
          <AddTable type="button" onClick={() => setShowModalEdit(true)}>
            <FontAwesomeIcon icon={faPlus} /> Table
          </AddTable>
        )}

      {showModalEdit && facility?.id && (
        <ModalCustomTable
          visible={showModalEdit}
          setVisible={state => {
            if (!state) setTarget(null);
            setShowModalEdit(state);
          }}
          facilityId={facility.id}
          target={target}
          setTarget={setTarget}
          tables={
            tables
              ? tables.map(table => ({name: table.label, value: table.name, order: table.order}))
              : []
          }
          reload={() => getCustomTables(null, true)}
        />
      )}

      {showModalViewAll && target && (
        <ModalViewAllRows
          visible={showModalViewAll}
          setVisible={state => {
            if (!state) {
              currentTableUpdater.current = undefined;
              setTarget(null);
            }
            setShowModalViewAll(state);
          }}
          target={target}
          inGlobalTables={inGlobalTables}
          reload={currentTableUpdater.current}
        />
      )}

      {showModalPreview && targetRow && (
        <ModalRowPreview
          visible={showModalPreview}
          setVisible={setShowModalPreview}
          selected={targetRow}
          setSelected={setTargetRow}
        />
      )}
    </>
  );
};

CustomTables.propTypes = {
  facility: PropTypes.objectOf(PropTypes.any),
  inGlobalTables: PropTypes.bool,
  setHasNoTables: PropTypes.func,
  query: PropTypes.string,
  globalToggleAll: PropTypes.bool,
  setGlobalToggleAll: PropTypes.func,
  setGlobalAllOpen: PropTypes.func,
  actionMenuEl: PropTypes.object
};

// Style Overrides
const OutermostWrapper = styled.div`
  ${flex("column", "nowrap")}

  margin-bottom: ${pad * 2}px;
`;

const InlineMenu = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: ${pad}px;
`;

const ActionsMenuWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: stretch;
`;

const ActionMenuButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-width: 36px;
  min-height: 32px;
  padding-left: ${pad}px;
  color: ${({theme}) => theme.secondary};
  background-color: transparent;
  transition: ${transition};

  svg {
    fill: ${({theme}) => theme.secondary};
  }

  &:hover {
    background-color: ${({theme}) => theme.secondary};
    color: ${({theme}) => theme.tertiary};

    svg {
      fill: ${({theme}) => theme.tertiary};
    }
  }

  ${({$active, theme}) =>
    $active &&
    `
      background-color: ${theme.secondary};
      color: ${theme.tertiary};

      svg {
        fill: ${theme.tertiary};
      }
    `}
`;

const ActionsMenu = styled.div`
  position: absolute;
  top: calc(100% + ${pad / 2}px);
  right: 0;
  min-width: 220px;
  background: ${({theme}) => theme.tertiary};
  border: ${border} solid ${({theme}) => theme.secondary};
  border-radius: ${radius};
  box-shadow: 0 3px 5px rgb(0, 0, 0, 0.5);
  z-index: ${z("higher")};
  overflow: hidden;
`;

const MenuSearch = styled.div`
  padding: ${pad / 2}px;

  ${SearchWrapper} {
    width: 100%;
  }

  ${SearchIcon} {
    top: 50%;
    transform: translateY(-50%);
  }
`;

const ActionItem = styled.button`
  ${flex("row", "nowrap", "start", "center")};
  gap: ${pad / 2}px;
  width: 100%;
  padding: ${pad / 2}px ${pad}px;
  background: ${({theme}) => theme.tertiary};
  color: ${({theme}) => theme.secondary};
  text-align: left;

  &:hover:not(:disabled) {
    color: ${({theme}) => theme.tertiary};
    background: ${({theme}) => theme.secondary};
  }
`;

const StyledSearch = styled(Search)`
  padding-left: 30px;

  &[type="text"] {
    max-width: 100%;
    width: 100%;
  }
`;

const AddTable = styled(Button)`
  float: right;
`;

const LoadWrapper = styled.div`
  position: relative;
  width: 100%;
  min-height: 20px;
  opacity: ${({show}) => (show ? 1 : 0)};

  div {
    margin-top: 0;
  }
`;

const NotFound = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 85%;
  min-height: 75px;
  padding: ${pad}px;
  margin-right: ${pad}px;
  border-radius: ${radius};
  border: ${border} solid ${({theme}) => theme.secondary};
  width: 100%;
`;

export default CustomTables;
